import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './BookingBoard.css';

// Booking board — every flat of every wing, floor by floor, series 1..N.
// Colour = status; left band = flat type; refuge/podium/amenity floors and
// jodi pairs come from the project's board layout (settings).
const STATUS = {
  available: { label: 'Available', cls: 'st-av' },
  hold: { label: 'On hold', cls: 'st-hold' },
  sold: { label: 'Booked', cls: 'st-sold' },
  blocked: { label: 'Blocked', cls: 'st-hold' },
  payment_pending: { label: 'Payment pending', cls: 'st-hold' },
};
const TYPE_COLORS = ['#0ea5e9', '#6366f1', '#14b8a6', '#f59e0b', '#ec4899', '#84cc16', '#8b5cf6', '#f97316', '#06b6d4', '#ef4444', '#64748b'];
const fmtArea = (a) => (a ? Number(a).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—');
const inr = (v) => { const x = Number(v) || 0; return x >= 1e7 ? `₹${(x / 1e7).toFixed(2)} Cr` : x >= 1e5 ? `₹${(x / 1e5).toFixed(1)} L` : x ? `₹${x.toLocaleString('en-IN')}` : '—'; };
const wingOf = (u) => String(u.tower_name || '').replace(/^Wing\s*/i, '');
const flatNo = (floor, series) => `${floor}${String(series).padStart(2, '0')}`;
const ordinal = (n) => { const s = ['th', 'st', 'nd', 'rd']; const v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };

const BookingBoard = ({ apiBase, user }) => {
  const [data, setData] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [error, setError] = useState('');
  const [wing, setWing] = useState('all');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showJodi, setShowJodi] = useState(false);
  const [sel, setSel] = useState(null);
  const [toast, setToast] = useState('');

  const load = useCallback(() => {
    axios.get(`${apiBase}/booking-flow/board`, { params: projectId ? { project_id: projectId } : {} })
      .then((r) => { setData(r.data); if (!projectId && r.data.project) setProjectId(r.data.project.id); })
      .catch((e) => setError(e.response?.status === 403 ? 'Your role cannot view the booking board.' : 'Could not load the booking board.'));
  }, [apiBase, projectId]);
  useEffect(() => { load(); }, [load]);

  const layout = useMemo(() => data?.layout || {}, [data]);
  const units = useMemo(() => data?.units || [], [data]);
  const seriesCount = Number(layout.series_count) || Math.max(10, ...units.map((u) => Number(u.unit_number) || 0));
  const series = Array.from({ length: seriesCount }, (_, i) => i + 1);
  const refuge = new Set(layout.refuge_floors || []);
  const podium = new Set(layout.podium_floors || []);
  const amenity = new Set(layout.amenity_floors || []);
  const jodi = layout.jodi || [];
  const jodiOf = (s) => jodi.findIndex((j) => (j.flats || []).includes(s));

  // Type of a flat: 3BHK-style variants override the series default.
  const typeOf = useCallback((u, s) => {
    const v = u && layout.variants && layout.variants[u.bhk_type];
    if (v) return v;
    return (layout.series || {})[s] || (u ? { type: u.bhk_type, area: u.carpet_area } : null);
  }, [layout]);

  const types = useMemo(() => {
    const list = [];
    Object.values(layout.series || {}).concat(Object.values(layout.variants || {})).forEach((t) => {
      if (t && !list.find((x) => x.type === t.type)) list.push({ type: t.type, area: t.area });
    });
    return list.sort((a, b) => a.type.localeCompare(b.type, undefined, { numeric: true }));
  }, [layout]);
  const typeColor = (t) => TYPE_COLORS[Math.max(0, types.findIndex((x) => x.type === t)) % TYPE_COLORS.length];

  const byKey = useMemo(() => {
    const m = {};
    units.forEach((u) => { if (/^\d+$/.test(String(u.unit_number))) m[`${wingOf(u)}|${u.floor_number}|${Number(u.unit_number)}`] = u; });
    return m;
  }, [units]);
  const wings = useMemo(() => [...new Set(units.map(wingOf))].filter(Boolean).sort(), [units]);
  const floors = useMemo(() => {
    const fl = units.map((u) => Number(u.floor_number)).filter(Boolean);
    const min = layout.floors?.min || Math.min(...fl), max = layout.floors?.max || Math.max(...fl);
    const out = []; for (let f = max; f >= min; f--) out.push(f); return out;
  }, [units, layout]);

  const saleable = (u) => u && !/^(refuge|not.?for.?sale)$/i.test(u.status || '');
  const statusKey = (u) => (u?.status === 'blocked' || u?.status === 'payment_pending' ? 'hold' : u?.status || 'available');

  // Headline numbers + per-type summary
  const stats = useMemo(() => {
    const t = { total: 0, available: 0, hold: 0, sold: 0, availArea: 0, byType: {} };
    Object.values(byKey).forEach((u) => {
      if (!saleable(u) || (wing !== 'all' && wingOf(u) !== wing)) return;
      const st = statusKey(u), ty = typeOf(u, Number(u.unit_number))?.type || u.bhk_type;
      t.total++; t[st] = (t[st] || 0) + 1;
      if (st === 'available') t.availArea += Number(u.carpet_area) || 0;
      const b = (t.byType[ty] = t.byType[ty] || { total: 0, available: 0, hold: 0, sold: 0 });
      b.total++; b[st] = (b[st] || 0) + 1;
    });
    return t;
  }, [byKey, wing, typeOf]);

  const jodiAvail = useMemo(() => jodi.map((j) => {
    let n = 0;
    wings.forEach((w) => floors.forEach((f) => {
      if (wing !== 'all' && w !== wing) return;
      const us = (j.flats || []).map((s) => byKey[`${w}|${f}|${s}`]);
      if (us.every((u) => saleable(u) && statusKey(u) === 'available')) n++;
    }));
    return n;
  }), [jodi, wings, floors, byKey, wing]);

  const rate = Number(data?.project?.rate_per_sqft) || 0;
  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 5000); };

  if (error) return <div className="bb"><div className="bb-error">{error}</div></div>;
  if (!data) return <div className="bb"><div className="bb-loading">Loading the booking board…</div></div>;
  if (!units.length) return <div className="bb"><div className="bb-loading">No flats set up for this project yet.</div></div>;

  const shownWings = wing === 'all' ? wings : [wing];
  const pass = (u, s) => {
    if (!u) return true;
    if (typeFilter && typeOf(u, s)?.type !== typeFilter) return false;
    if (statusFilter && statusKey(u) !== statusFilter) return false;
    return true;
  };

  return (
    <div className="bb">
      <div className="bb-head">
        <div>
          <h1>Booking board</h1>
          <div className="bb-sub">{data.project?.name}{rate ? ` · ₹${rate.toLocaleString('en-IN')} / sq ft` : ''}</div>
        </div>
        {data.projects?.length > 1 && (
          <select value={projectId || ''} onChange={(e) => setProjectId(Number(e.target.value))}>
            {data.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
      </div>

      <div className="bb-kpis">
        <div className="bb-kpi"><span>Saleable flats</span><b>{stats.total}</b></div>
        <div className="bb-kpi k-av"><span>Available</span><b>{stats.available}</b><small>{fmtArea(stats.availArea)} sq ft{rate ? ` · ${inr(stats.availArea * rate)}` : ''}</small></div>
        <div className="bb-kpi k-hold"><span>On hold</span><b>{stats.hold || 0}</b></div>
        <div className="bb-kpi k-sold"><span>Booked</span><b>{stats.sold || 0}</b><small>{stats.total ? Math.round(((stats.sold || 0) * 100) / stats.total) : 0}% sold</small></div>
        {jodi.map((j, i) => (
          <div key={j.name} className="bb-kpi k-jodi"><span>{j.name} · flats {j.flats.join(' + ')}</span><b>{jodiAvail[i]}</b><small>{fmtArea(j.area)} sq ft · pairs free</small></div>
        ))}
      </div>

      <div className="bb-bar">
        <div className="bb-seg">
          {['all', ...wings].map((w) => (
            <button key={w} className={wing === w ? 'on' : ''} onClick={() => setWing(w)}>{w === 'all' ? 'All wings' : `Wing ${w}`}</button>
          ))}
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">All flat types</option>
          {types.map((t) => <option key={t.type} value={t.type}>{t.type} · {fmtArea(t.area)} sq ft</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="available">Available</option><option value="hold">On hold</option><option value="sold">Booked</option>
        </select>
        {jodi.length > 0 && (
          <label className="bb-check"><input type="checkbox" checked={showJodi} onChange={(e) => setShowJodi(e.target.checked)} /> Show jodi pairs</label>
        )}
      </div>

      <div className="bb-legend">
        <div className="bb-leg-group">
          <b>Status</b>
          <span><i className="sw st-av" />Available</span><span><i className="sw st-hold" />On hold</span>
          <span><i className="sw st-sold" />Booked</span><span><i className="sw st-na" />Not for sale</span>
        </div>
        <div className="bb-leg-group">
          <b>Floors</b>
          {refuge.size > 0 && <span><i className="tag t-ref">R</i>Refuge {[...refuge].map(ordinal).join(', ')}</span>}
          {podium.size > 0 && <span><i className="tag t-pod">P</i>Podium parking {ordinal(Math.min(...podium))}–{ordinal(Math.max(...podium))}</span>}
          {amenity.size > 0 && <span><i className="tag t-am">A</i>Amenities & fitness {[...amenity].map(ordinal).join(', ')}</span>}
        </div>
        <div className="bb-leg-group bb-leg-types">
          <b>Flat types</b>
          {types.map((t) => <span key={t.type}><i className="band" style={{ background: typeColor(t.type) }} />{t.type} · {fmtArea(t.area)}</span>)}
        </div>
      </div>

      <div className="bb-wings">
        {shownWings.map((w) => (
          <div key={w} className="bb-wing">
            <div className="bb-wing-title">Wing {w}</div>
            <div className="bb-scroll">
              <table className="bb-grid">
                <thead>
                  <tr>
                    <th className="bb-fl">Floor</th>
                    {series.map((s) => {
                      const t = (layout.series || {})[s];
                      const j = jodiOf(s);
                      return (
                        <th key={s} className={showJodi && j >= 0 ? `jh j${j}` : ''}>
                          <div className="bb-sn">{s}</div>
                          {t && <div className="bb-st" style={{ color: typeColor(t.type) }}>{t.type.replace(' - ', ' ')}</div>}
                          {showJodi && j >= 0 && <div className="bb-jt">{jodi[j].name}</div>}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {floors.map((f) => (
                    <tr key={f} className={refuge.has(f) ? 'r-ref' : amenity.has(f) ? 'r-am' : ''}>
                      <th className="bb-fl">
                        <div>{ordinal(f)}</div>
                        <div className="bb-ftags">
                          {refuge.has(f) && <i className="tag t-ref" title="Refuge floor">R</i>}
                          {podium.has(f) && <i className="tag t-pod" title="Podium parking">P</i>}
                          {amenity.has(f) && <i className="tag t-am" title="Amenities & fitness">A</i>}
                        </div>
                      </th>
                      {series.map((s) => {
                        const u = byKey[`${w}|${f}|${s}`];
                        const j = jodiOf(s);
                        const jcls = showJodi && j >= 0 ? ` jc j${j}` : '';
                        if (!saleable(u)) {
                          const why = (layout.space_labels || {})[f]?.[s] || (refuge.has(f) ? 'Refuge' : podium.has(f) ? 'Podium' : amenity.has(f) ? 'Amenity' : '—');
                          return <td key={s} className={`bb-cell st-na${jcls}`}><span className="na">{why}</span></td>;
                        }
                        const t = typeOf(u, s);
                        const st = statusKey(u);
                        const dim = !pass(u, s);
                        return (
                          <td key={s} className={`bb-cell ${STATUS[st]?.cls || 'st-av'}${jcls}${dim ? ' dim' : ''}${sel?.id === u.id ? ' selected' : ''}`}
                            onClick={() => setSel(u)} title={`${w}-${flatNo(f, s)} · ${t?.type || u.bhk_type} · ${fmtArea(u.carpet_area)} sq ft · ${STATUS[st]?.label || st}`}>
                            <i className="band" style={{ background: typeColor(t?.type) }} />
                            <div className="no">{flatNo(f, s)}</div>
                            <div className="ar">{fmtArea(u.carpet_area)}</div>
                            {u.booking_number && <div className="bk">{u.booking_number}</div>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      <h3 className="bb-h3">By flat type{wing !== 'all' ? ` · Wing ${wing}` : ''}</h3>
      <div className="bb-table-wrap">
        <table className="bb-sum">
          <thead><tr><th>Flat type</th><th>Carpet (sq ft)</th><th>Total</th><th>Available</th><th>On hold</th><th>Booked</th><th>Sold %</th></tr></thead>
          <tbody>
            {types.map((t) => {
              const b = stats.byType[t.type] || { total: 0 };
              return (
                <tr key={t.type} onClick={() => setTypeFilter(typeFilter === t.type ? '' : t.type)} className={typeFilter === t.type ? 'on' : ''}>
                  <td><i className="band" style={{ background: typeColor(t.type) }} />{t.type}</td>
                  <td>{fmtArea(t.area)}</td><td>{b.total}</td><td>{b.available || 0}</td><td>{b.hold || 0}</td><td>{b.sold || 0}</td>
                  <td>{b.total ? Math.round(((b.sold || 0) * 100) / b.total) : 0}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {sel && (
        <FlatPanel apiBase={apiBase} unit={sel} layout={layout} typeOf={typeOf} byKey={byKey} saleable={saleable} statusKey={statusKey}
          rate={rate} onClose={() => setSel(null)}
          onDone={(m) => { setSel(null); flash(m); load(); }} />
      )}
      {toast && <div className="bb-toast">{toast}</div>}
    </div>
  );
};

// Side panel: flat facts, current booking, and Book / Hold / Release.
const FlatPanel = ({ apiBase, unit, layout, typeOf, byKey, saleable, statusKey, rate, onClose, onDone }) => {
  const w = wingOf(unit), f = Number(unit.floor_number), s = Number(unit.unit_number);
  const t = typeOf(unit, s);
  const st = statusKey(unit);
  const jodi = (layout.jodi || []).find((j) => (j.flats || []).includes(s));
  const partnerS = jodi ? jodi.flats.find((x) => x !== s) : null;
  const partner = partnerS ? byKey[`${w}|${f}|${partnerS}`] : null;
  const partnerFree = partner && saleable(partner) && statusKey(partner) === 'available';

  const [mode, setMode] = useState(null); // 'book' | 'release'
  const [brokers, setBrokers] = useState([]);
  const [form, setForm] = useState({
    client_name: '', client_phone: '', client_email: '', broker_mode: 'direct', channel_partner_id: '',
    broker_name: '', broker_firm: '', broker_phone: '', form_number: '', booking_date: new Date().toISOString().slice(0, 10),
    booking_amount: '', status: 'hold', with_jodi: false, notes: '',
  });
  const [reason, setReason] = useState('');
  const [approver, setApprover] = useState(null);
  useEffect(() => { axios.get(`${apiBase}/booking-approval/me`).then((r) => setApprover(r.data.approver)).catch(() => {}); }, [apiBase]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { if (mode === 'book') axios.get(`${apiBase}/booking-flow/brokers`).then((r) => setBrokers(r.data || [])).catch(() => {}); }, [mode, apiBase]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const book = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim()) { setErr('Client name is required'); return; }
    if (String(form.client_phone).replace(/\D/g, '').length < 10) { setErr('Enter the client\'s 10-digit mobile'); return; }
    if (form.broker_mode === 'existing' && !form.channel_partner_id) { setErr('Pick the channel partner'); return; }
    if (form.broker_mode === 'new' && !form.broker_name.trim()) { setErr('Enter the channel partner name'); return; }
    setBusy(true); setErr('');
    const broker = form.broker_mode === 'existing' ? { channel_partner_id: Number(form.channel_partner_id) }
      : form.broker_mode === 'new' ? { name: form.broker_name, firm: form.broker_firm, phone: form.broker_phone } : null;
    try {
      const r = await axios.post(`${apiBase}/booking-flow/book`, {
        client: { name: form.client_name, phone: form.client_phone, email: form.client_email }, broker,
        project_id: unit.project_id || undefined, unit_ids: form.with_jodi && partnerFree ? [unit.id, partner.id] : [unit.id],
        form_number: form.form_number, booking_date: form.booking_date, visit_date: form.booking_date,
        booking_amount: form.booking_amount, status: form.status, notes: form.notes,
      });
      onDone(`${form.status === 'booked' ? 'Booked' : 'Held'} ${r.data.sales.map((x) => x.flat).join(' + ')} for ${form.client_name}`);
    } catch (e2) {
      setErr(e2.response?.status === 403 ? 'Your role cannot record bookings.' : (e2.response?.data?.error || 'Could not record the booking'));
    } finally { setBusy(false); }
  };

  const decideApproval = async (decision) => {
    if (!window.confirm(decision === 'approve' ? `Approve this booking as ${approver.name}? Your name and the time are recorded and cannot be changed.` : 'Reject this booking? The flat goes back to available.')) return;
    setBusy(true); setErr('');
    try { const r = await axios.post(`${apiBase}/booking-approval/${unit.sale_id}/decide`, { decision }); onDone(r.data.message); }
    catch (e2) { setErr(e2.response?.data?.message || e2.response?.data?.error || 'Could not record the decision'); }
    finally { setBusy(false); }
  };

  const release = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { setErr('Give a reason'); return; }
    setBusy(true); setErr('');
    try { await axios.post(`${apiBase}/booking-flow/release`, { unit_id: unit.id, reason }); onDone(`Released ${w}-${flatNo(f, s)}`); }
    catch (e2) { setErr(e2.response?.status === 403 ? 'Only admin or sales head can release a flat.' : (e2.response?.data?.error || 'Could not release')); }
    finally { setBusy(false); }
  };

  const row = (k, v) => <div className="fp-row"><span>{k}</span><b>{v}</b></div>;
  return (
    <div className="fp-back" onClick={() => !busy && onClose()}>
      <aside className="fp" onClick={(e) => e.stopPropagation()}>
        <div className="fp-head">
          <div>
            <div className="fp-title">Flat {w}-{flatNo(f, s)}</div>
            <div className={`fp-status ${STATUS[st]?.cls || ''}`}>{STATUS[st]?.label || st}</div>
          </div>
          <button className="fp-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        {row('Type', t?.type || unit.bhk_type)}
        {row('Carpet area', `${fmtArea(unit.carpet_area)} sq ft`)}
        {row('Floor', `${ordinal(f)} · Wing ${w}`)}
        {rate ? row('Base value', inr(Number(unit.carpet_area) * rate)) : null}
        {jodi && row(jodi.name, `with flat ${w}-${flatNo(f, partnerS)} · ${fmtArea(jodi.area)} sq ft · ${partnerFree ? 'partner free' : 'partner taken'}`)}

        {unit.sale_id && (
          <div className="fp-box">
            <div className="fp-box-t">Booking</div>
            {row('Form no.', unit.booking_number || '—')}
            {row('Client', unit.client_id ? <Link to={`/clients/${unit.client_id}`}>{unit.client_name || `#${unit.client_id}`}</Link> : '—')}
            {row('Channel partner', unit.broker_name ? `${unit.broker_name}${unit.broker_firm ? ` · ${unit.broker_firm}` : ''}` : 'Direct')}
            {row('Date', unit.sale_date ? new Date(unit.sale_date).toLocaleDateString('en-IN') : '—')}
            {row('Cheques on file', unit.cheques || 0)}
            {row('Partner approval', unit.approval_status === 'approved' ? `✅ ${unit.approved_by_name} · ${new Date(unit.approved_at).toLocaleString('en-IN')} · ${unit.approval_via === 'whatsapp' ? 'WhatsApp' : 'CRM'}`
              : unit.approval_status === 'legacy' ? '✅ Manual entry (before the system)' : unit.approval_status === 'pending' ? '⏳ Awaiting a partner' : unit.approval_status || '—')}
            {unit.approval_status === 'pending' && approver && (
              <div className="fp-actions">
                <button className="btn btn-primary" style={{ background: '#16a34a' }} disabled={busy} onClick={() => decideApproval('approve')}>✅ Approve as {approver.name}</button>
                <button className="btn btn-danger" disabled={busy} onClick={() => decideApproval('reject')}>❌ Reject</button>
              </div>
            )}
          </div>
        )}

        {!mode && st === 'available' && (
          <div className="fp-actions">
            <button className="btn btn-primary" onClick={() => { setForm({ ...form, status: 'hold' }); setMode('book'); }}>Hold</button>
            <button className="btn btn-primary" onClick={() => { setForm({ ...form, status: 'booked' }); setMode('book'); }}>Book</button>
          </div>
        )}
        {!mode && st !== 'available' && (
          <div className="fp-actions">
            {unit.client_id && <Link className="btn btn-secondary" to={`/clients/${unit.client_id}`}>Open client (KYC, cheques)</Link>}
            <button className="btn btn-danger" onClick={() => setMode('release')}>Release flat</button>
          </div>
        )}

        {mode === 'book' && (
          <form className="fp-form" onSubmit={book}>
            <div className="fp-box-t">{form.status === 'booked' ? 'Book (cheque received)' : 'Hold (no cheque yet)'}</div>
            <label>Client name *<input value={form.client_name} onChange={set('client_name')} autoFocus /></label>
            <label>Mobile *<input value={form.client_phone} onChange={set('client_phone')} inputMode="tel" /></label>
            <label>Email<input type="email" value={form.client_email} onChange={set('client_email')} /></label>
            <label>Came through
              <select value={form.broker_mode} onChange={set('broker_mode')}>
                <option value="direct">Direct</option><option value="existing">Channel partner (existing)</option><option value="new">Channel partner (new)</option>
              </select>
            </label>
            {form.broker_mode === 'existing' && (
              <label>Channel partner
                <select value={form.channel_partner_id} onChange={set('channel_partner_id')}>
                  <option value="">—</option>
                  {brokers.map((b) => <option key={b.id} value={b.id}>{b.name}{b.firm ? ` · ${b.firm}` : ''}</option>)}
                </select>
              </label>
            )}
            {form.broker_mode === 'new' && (
              <>
                <label>Partner name *<input value={form.broker_name} onChange={set('broker_name')} /></label>
                <label>Firm<input value={form.broker_firm} onChange={set('broker_firm')} /></label>
                <label>Partner mobile<input value={form.broker_phone} onChange={set('broker_phone')} inputMode="tel" /></label>
              </>
            )}
            <div className="fp-two">
              <label>Form no.<input value={form.form_number} onChange={set('form_number')} placeholder="K000xx" /></label>
              <label>Date<input type="date" value={form.booking_date} onChange={set('booking_date')} /></label>
            </div>
            <label>Booking amount (₹)<input type="number" min="0" value={form.booking_amount} onChange={set('booking_amount')} /></label>
            <label>Status
              <select value={form.status} onChange={set('status')}><option value="hold">Hold (no cheque yet)</option><option value="booked">Booked (cheque received)</option></select>
            </label>
            {jodi && partnerFree && (
              <label className="fp-check"><input type="checkbox" checked={form.with_jodi} onChange={set('with_jodi')} /> Book as {jodi.name}: also take {w}-{flatNo(f, partnerS)} ({fmtArea(jodi.area)} sq ft total)</label>
            )}
            <label>Notes<textarea value={form.notes} onChange={set('notes')} /></label>
            {err && <div className="fp-err">{err}</div>}
            <div className="fp-actions">
              <button type="button" className="btn btn-secondary" onClick={() => { setMode(null); setErr(''); }} disabled={busy}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : form.status === 'booked' ? 'Record booking' : 'Put on hold'}</button>
            </div>
            <div className="fp-note">Creates the client, the channel partner (if new), the walk-in and the sale, and marks the flat. No message is sent.</div>
          </form>
        )}

        {mode === 'release' && (
          <form className="fp-form" onSubmit={release}>
            <div className="fp-box-t">Release this flat</div>
            <label>Reason *<textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. cheque bounced, client withdrew" autoFocus /></label>
            {err && <div className="fp-err">{err}</div>}
            <div className="fp-actions">
              <button type="button" className="btn btn-secondary" onClick={() => { setMode(null); setErr(''); }} disabled={busy}>Cancel</button>
              <button type="submit" className="btn btn-danger" disabled={busy}>{busy ? 'Releasing…' : 'Release to available'}</button>
            </div>
            <div className="fp-note">The sale is kept as cancelled for history; the flat goes back to available.</div>
          </form>
        )}
      </aside>
    </div>
  );
};

export default BookingBoard;
